import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { CHECKOUT } from "@/constants/testIds";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState({ phase: "paid", data: r.data });
          return;
        }
        if (r.data.status === "expired") {
          setState({ phase: "expired", data: r.data });
          return;
        }
        if (attempts.current >= 8) {
          setState({ phase: "timeout", data: r.data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ phase: "error", data: null });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-24">
      {state.phase === "polling" && (
        <div data-testid={CHECKOUT.polling} className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-900 mx-auto animate-spin mb-6" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Confirming your payment…</h1>
          <p className="text-slate-500 mt-2">This usually takes a few seconds.</p>
        </div>
      )}

      {state.phase === "paid" && (
        <div data-testid={CHECKOUT.success} className="text-center">
          <div data-testid={CHECKOUT.successBadge} className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-700" />
          </div>
          <p className="overline text-emerald-800 mb-2">Payment received</p>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
            Thank you — your order is confirmed.
          </h1>
          <p className="text-slate-600 mt-4 max-w-xl mx-auto">
            We've notified the distributor and they'll ship your supplies within 24 hours.
            You'll receive tracking details by email.
          </p>
          <div className="mt-8 inline-block bg-slate-50 border border-slate-200 rounded-sm px-6 py-4 text-left">
            <p className="text-xs text-slate-500">Order total</p>
            <p className="font-display text-2xl font-black text-slate-900">
              {formatMoney((state.data.amount_total || 0) / 100)}
            </p>
            <p className="text-xs text-slate-500 mt-2">Order ID: {state.data.order_id}</p>
          </div>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/account" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800">
              View my orders <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/shop" className="px-6 py-3 border border-slate-300 text-slate-900 text-sm font-semibold rounded-sm hover:border-slate-900">
              Continue shopping
            </Link>
          </div>
        </div>
      )}

      {(state.phase === "error" || state.phase === "expired" || state.phase === "timeout") && (
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-slate-900">
            {state.phase === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}
          </h1>
          <p className="text-slate-500 mt-2">
            If your card was charged, please contact us — we'll sort it out immediately.
          </p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/cart" className="px-6 py-3 bg-emerald-900 text-white text-sm rounded-sm">
              Return to cart
            </Link>
            <Link to="/contact" className="px-6 py-3 border border-slate-300 text-slate-900 text-sm rounded-sm">
              Contact support
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
